import React, { useState, useEffect, useMemo, useRef } from 'react';
// import { initializeApp } from 'firebase/app';
// import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
// import { getFirestore, collection, addDoc, onSnapshot, query, serverTimestamp, updateDoc, doc, deleteDoc, where, getDocs, writeBatch } from 'firebase/firestore';
import { Building, Truck, Warehouse, LogOut, PlusCircle, Search, X, Clock, BarChart2, List, Download, Calendar, FileCheck, XCircle, Trash2, Upload, Undo2, Pencil, ChevronDown, Filter, Scale } from 'lucide-react'; // Added Filter & Scale icons
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle, fetchVehiculoPorCarga, createEntrega, updateEntrega, deleteEntrega, fetchEntregas, normalizeToYYYYMMDD } from './services/api';
import { loginWithAzure, handleAuthCallback, logout } from './services/auth'; // Importar servicio de autenticación

// --- Configuración de Firebase ---
// ELIMINADO: Firebase ha sido desinstalado.

// --- Constantes de la Aplicación ---
// Listas predefinidas para selects y validaciones
const operacionesList = ["Local", "Materia Prima", "Nacional PT", "Exportación", "Importación", "Devolución", "Gráneles", "MEII's", "Proyectos", "Alm. Repuestos", "Otros", "Ultima Milla"];
const procesosList = ["Cargue", "Descargue"];
const tiposVehiculoList = ["LUV - 1,4 TN", "TURBO - 4,5 TN", "SENCILLO - 8 TN", "DOBLETROQUE - 17 TN", "SUPERPODEROSA - 22 TN", "MINIMULA - 20 TN", "TRACTOMULA 2 EJES - 32 TN", "TRACTOMULA 3 EJES - 35 TN", "CONTENEDOR 20'", "CONTENEDOR 40'"];

// Configuración visual (Colores para gráficos)
const COLORS = ['#3a773c', '#99c754', '#f3b42f', '#7CB82F', '#A5A5A5', '#FF1943', '#19A2FF'];

// Definición de sedes disponibles
const LOCATIONS = {
    BOGOTA: 'Bogotá',
    FUNZA: 'Funza',
    BUGA: 'Buga',
    BARRANQUILLA: 'Barranquilla'
};

// --- Hooks y Utilidades ---

// Formatea segundos a formato HH:MM:SS
const formatDuration = (totalSeconds) => {
    if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00:00';
    const totalSecondsRounded = Math.round(totalSeconds);
    const hours = Math.floor(totalSecondsRounded / 3600);
    const minutes = Math.floor((totalSecondsRounded % 3600) / 60);
    const seconds = totalSecondsRounded % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Hook personalizado para cronómetros
// Actualiza el tiempo transcurrido cada segundo desde startTime
const useTimer = (startTime) => {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!startTime) { setElapsed(0); return; }
        // Maneja formatos de timestamp antiguos (Firebase) o Date estándar
        let startMs = 0;
        if (startTime.seconds) {
            startMs = startTime.seconds * 1000;
        } else if (startTime instanceof Date) {
            startMs = startTime.getTime();
        } else if (typeof startTime === 'string' || typeof startTime === 'number') {
            startMs = new Date(startTime).getTime();
        } else {
            return;
        }

        // Calculate initial elapsed time immediately
        setElapsed(Math.floor((Date.now() - startMs) / 1000));

        const interval = setInterval(() => { setElapsed(Math.floor((Date.now() - startMs) / 1000)); }, 1000);
        return () => clearInterval(interval);
    }, [startTime]);
    return formatDuration(elapsed);
};

// Obtiene la fecha actual en formato YYYY-MM-DD
const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatExcelDate = (val) => normalizeToYYYYMMDD(val);

// --- COMPONENTE DE CARGA MASIVA ---
// Componente importado internamente para simplificar (idealmente debería estar en archivo separado)
const cargaMasivaStyles = `
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    .fade-in {
        animation: fadeIn 0.5s ease-out forwards;
    }
`;

function CargaMasiva({ onScheduleMassive }) {
    const [jsonData, setJsonData] = useState([]);
    const [fileName, setFileName] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const fileInputRef = useRef(null);

    // Carga dinámica de SheetJS
    useEffect(() => {
        const scriptId = 'xlsx-script';
        if (document.getElementById(scriptId)) {
            setIsLoading(false);
            return;
        }
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        script.async = true;
        script.onload = () => setIsLoading(false);
        script.onerror = () => {
            console.error("Error al cargar la biblioteca SheetJS.");
            setToastMessage("Error crítico: No se pudo cargar el lector de archivos.");
            setIsLoading(false);
        };
        document.body.appendChild(script);
    }, []);

    // Procesa el archivo Excel seleccionado
    const handleFile = (file) => {
        if (!file || !window.XLSX) {
            alert("El lector de archivos no está listo. Intente de nuevo en unos segundos.");
            return;
        }
        setFileName(`Archivo seleccionado: ${file.name}`);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = window.XLSX.read(data, { type: 'array', cellDates: true, raw: false });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const dataFromJson = window.XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd' });
                const formattedData = dataFromJson.map(row => {
                    const newRow = { ...row };
                    for (const key of Object.keys(newRow)) {
                        if (/fecha|vencimiento|cita/i.test(key)) {
                            const formatted = formatExcelDate(newRow[key]);
                            if (formatted !== null) {
                                newRow[key] = formatted;
                            }
                        }
                    }
                    return newRow;
                });
                setJsonData(formattedData);
            } catch (error) {
                console.error("Error al procesar el archivo Excel:", error);
                alert("Hubo un error al leer el archivo. Asegúrate de que el formato sea correcto.");
                resetUI();
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleFileChange = (event) => handleFile(event.target.files[0]);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files.length > 0) handleFile(e.dataTransfer.files[0]);
    };

    const handleSubmit = async () => {
        if (jsonData.length > 0) {
            const success = await onScheduleMassive(jsonData);
            if (success) {
                resetUI();
            }
        }
    };

    const downloadTemplate = () => {
        if (!window.XLSX) {
            alert("El generador de plantillas no está listo. Intente de nuevo en unos segundos.");
            return;
        }
        const sampleData = [{ Ubicacion: "Bogotá o Funza", Placa: "ABC-123", Conductor: "Juan Perez", DocumentoConductor: "12345678", Transportadora: "Transportes Veloz", Operacion: "Nacional PT", Proceso: "Cargue", TipoVehiculo: "TRACTOMULA 2 EJES - 32 TN", NumeroCarga: "C-5555", Cliente: "Cliente Ejemplo", Destino: "Cali", ARL: "ARL SURA", "Vencimiento ARL": "2025-12-31", FechaProgramacion: getTodayDateString(), PesoProgramado: "32", Observaciones: "Carga frágil" }];
        const worksheet = window.XLSX.utils.json_to_sheet(sampleData, { cellDates: true, dateNF: 'yyyy-mm-dd' });
        const workbook = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(workbook, worksheet, "Programacion");
        window.XLSX.writeFile(workbook, "Plantilla_Programacion_Patios.xlsx");
    };

    const resetUI = () => {
        setJsonData([]);
        setFileName("");
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <>
            <style>{cargaMasivaStyles}</style>
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <main className="p-6 md:p-8 rounded-2xl mb-8">
                    <h2 className="text-2xl font-semibold text-gray-700 mb-4 border-b pb-3">Carga Masiva de Programación desde Excel</h2>
                    {isLoading ? (<div className="text-center p-8 text-gray-500">Cargando lector de archivos...</div>) : (
                        <>
                            <div className={`p-8 text-center cursor-pointer border-2 dashed rounded-lg transition-all duration-300 ${isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} onClick={() => fileInputRef.current.click()} onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }} onDragLeave={() => setIsDragOver(false)} onDrop={handleDrop}>
                                <p className="text-gray-500">Arrastra y suelta tu archivo aquí o haz clic para seleccionar</p>
                                <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
                            </div>
                            <div className="mt-4 text-center"><p className="mt-2 text-gray-600 h-6">{fileName}</p></div>
                            <div className="mt-5 text-sm text-gray-500">
                                <p><span className="font-semibold">Instrucciones:</span> El archivo Excel debe contener las columnas definidas en la plantilla. Las columnas <code className="bg-gray-200 text-red-600 px-1 rounded mx-1">Placa</code> y <code className="bg-gray-200 text-red-600 px-1 rounded mx-1">FechaProgramacion</code> son obligatorias.</p>
                                <button onClick={downloadTemplate} className="text-blue-600 hover:underline mt-2 inline-block bg-transparent border-none p-0">Descargar plantilla de ejemplo</button>
                            </div>
                        </>
                    )}
                </main>

                {jsonData.length > 0 && (
                    <section className="p-6 md:p-8 rounded-2xl shadow-inner bg-gray-50 fade-in">
                        <h3 className="text-xl font-semibold text-gray-700 mb-4">Datos a Programar ({jsonData.length} vehículos)</h3>
                        <div className="overflow-x-auto h-[40vh]">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-100 sticky top-0"><tr>{Object.keys(jsonData[0]).map(header => <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{header}</th>)}</tr></thead>
                                <tbody className="bg-white divide-y divide-gray-200">{jsonData.map((row, index) => (<tr key={index}>{Object.values(row).map((cell, cellIndex) => <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-gray-700">{String(cell)}</td>)}</tr>))}</tbody>
                            </table>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button onClick={handleSubmit} className="bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition duration-300">Programar Todos</button>
                        </div>
                    </section>
                )}

                {toastMessage && (<div className="fixed bottom-5 right-5 bg-green-500 text-white py-3 px-5 rounded-lg shadow-xl fade-in">{toastMessage}</div>)}
            </div>
        </>
    );
}


// --- Componentes Reutilizables ---

// Modal Genérico
const Modal = ({ children, isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                {children}
            </div>
        </div>
    );
};

// Tarjeta de Vehículo (VehicleCard)
// Muestra la información resumida de un vehículo en el tablero kanban
// Incluye botones de acción según el estado actual
const VehicleCard = ({ vehicle, onAction, onUpdateField }) => {
    const { placa, status, proceso, operacion, muelle, timestamps, numeroCarga, observaciones, location } = vehicle;
    const [editedObservaciones, setEditedObservaciones] = useState(observaciones || '');
    
    // Guarda observaciones al perder el foco
    const handleObservacionesBlur = () => {
        if (editedObservaciones !== (observaciones || '')) {
            onUpdateField(vehicle.id, { observaciones: editedObservaciones });
        }
    };

    // Determina el timestamp de inicio para el cronómetro según el estado actual
    const getCurrentStatusStartTime = () => {
        switch(status) {
            case 'En Parqueadero':
            case 'Llegada OPL': return timestamps.llegadaParqueadero;
            case 'En Planta': return timestamps.ingresoPlanta;
            case 'Peso Inicial': return timestamps.ingresoBascula;
            case 'En Muelle': return timestamps.asignacionMuelle;
            case 'Cargando':
            case 'Descargando': return timestamps.inicioOperacion;
            case 'Pesaje Final': return timestamps.finOperacion;
            case 'En Despacho': return timestamps.inicioDespacho;
            default: return null;
        }
    };
    const timeInStatus = useTimer(getCurrentStatusStartTime());
    
    // Configuración visual (color y borde) según estado
    const statusInfo = {
        'Llegada OPL': { text: 'Llegada OPL', color: 'bg-gray-200 text-gray-800', border: 'border-gray-400' },
        'En Parqueadero': { text: 'En Parqueadero', color: 'bg-gray-200 text-gray-800', border: 'border-gray-400' },
        'En Planta': { text: 'En Planta', color: 'bg-yellow-100 text-yellow-800', border: 'border-yellow-500' },
        'Peso Inicial': { text: 'Peso Inicial', color: 'bg-orange-100 text-orange-800', border: 'border-orange-500' },
        'En Muelle': { text: `En Muelle ${muelle || ''}`, color: 'bg-green-100 text-green-800', border: 'border-green-500' },
        'Cargando': { text: `Cargando en Muelle ${muelle}`, color: 'bg-emerald-100 text-emerald-800', border: 'border-emerald-500' },
        'Descargando': { text: `Descargando en Muelle ${muelle}`, color: 'bg-emerald-100 text-emerald-800', border: 'border-emerald-500' },
        'Pesaje Final': { text: 'Pesaje Final', color: 'bg-teal-100 text-teal-800', border: 'border-teal-500' },
        'En Despacho': { text: 'En Despacho', color: 'bg-cyan-100 text-cyan-800', border: 'border-cyan-500' }
    }[status] || { text: status, color: 'bg-gray-100 text-gray-800', border: 'border-gray-300' };

    // Botón de acción genérico
    const ActionButton = ({ onClick, text, colorClass, fullWidth = true }) => (
        <button
            onClick={onClick}
            className={`
                ${fullWidth ? 'w-full' : ''} ${colorClass} text-white font-semibold
                py-1.5 px-2 text-xs rounded-md transition-all text-center
                hover:shadow-md hover:-translate-y-px whitespace-normal
            `}
        >
            {text}
        </button>
    );

    const isBogota = location !== LOCATIONS.FUNZA;

    return (
        <div className={`bg-white p-2.5 rounded-lg shadow-md border-l-4 ${statusInfo.border} mb-3 transition-shadow hover:shadow-lg relative`}>
            {/* Allow deletion in any state */}
             <button onClick={() => onAction('delete', vehicle)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500 transition-colors" title="Eliminar registro">
                 <Trash2 className="w-4 h-4" />
            </button>
            <div className="flex justify-between items-start mb-2">
                <div>
                    <p className="font-bold text-base text-gray-800 leading-tight">{numeroCarga || 'SIN N°'}</p>
                    <div className="text-xs text-gray-500 mt-1 flex items-center flex-wrap">
                        <span className="font-semibold bg-gray-100 px-1.5 py-0.5 rounded-full">{operacion}</span>
                        <span className="mx-1.5 text-gray-300">|</span>
                        <span>{proceso}</span>
                        <span className="mx-1.5 text-gray-300">|</span>
                        <span>Placa: <span className="font-semibold">{placa}</span></span>
                    </div>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center space-x-1 ${statusInfo.color}`}>{statusInfo.text}</span>
            </div>
            <div className="flex justify-between items-center mb-2 text-xs text-gray-700 bg-gray-50 p-1.5 rounded-md">
                <div className="flex items-center space-x-1.5"><Clock className="w-3 h-3 text-gray-500" /><span>T. Estado:</span></div>
                <span className="font-mono font-semibold">{timeInStatus}</span>
            </div>
            <div className="mb-2">
                <textarea
                    className="w-full p-1.5 border rounded text-xs bg-white focus:ring-1 focus:ring-blue-500"
                    value={editedObservaciones}
                    onChange={(e) => setEditedObservaciones(e.target.value)}
                    onBlur={handleObservacionesBlur}
                    placeholder="Observaciones..."
                    rows="1"
                ></textarea>
            </div>
            <div className="flex flex-col space-y-1.5">
                {/* Botones de acción principales según estado y ubicación */}
                <div className="flex space-x-2">
                    { (status === 'En Parqueadero' && isBogota) && <ActionButton onClick={() => onAction('enter_plant', vehicle)} text="Ingreso a Planta" colorClass="bg-[#f3b42f] hover:bg-yellow-500" /> }
                    { (status === 'Llegada OPL' && !isBogota) && <ActionButton onClick={() => onAction('move_to_dock_area', vehicle)} text="Ubicar en Muelle" colorClass="bg-[#99c754] hover:bg-lime-500" /> }
                    { status === 'En Planta' && <ActionButton onClick={() => onAction('weigh_vehicle', vehicle)} text="Iniciar Peso Inicial" colorClass="bg-[#99c754] hover:bg-lime-500" /> }
                    { status === 'Peso Inicial' && <ActionButton onClick={() => onAction('move_to_dock_area', vehicle)} text="Finalizar Peso Inicial" colorClass="bg-[#99c754] hover:bg-lime-500" /> }
                    { status === 'En Muelle' && (
                        !muelle
                        ? <ActionButton onClick={() => onAction('assign_dock', vehicle)} text="Asignar Muelle" colorClass="bg-[#f3b42f] hover:bg-yellow-500" />
                        : <ActionButton onClick={() => onAction('start_operation', vehicle)} text={`Iniciar ${proceso}`} colorClass="bg-[#3a773c] hover:bg-green-800" />
                    )}
                    { (status === 'Cargando' || status === 'Descargando') && <ActionButton onClick={() => onAction('end_operation', vehicle)} text="Finalizar Proceso" colorClass="bg-purple-500 hover:bg-purple-600" /> }
                    { (status === 'Pesaje Final' && isBogota) && <ActionButton onClick={() => onAction('final_weighing_complete', vehicle)} text="Completar Pesaje Final" colorClass="bg-teal-500 hover:bg-teal-600" /> }
                    { status === 'En Despacho' && <ActionButton onClick={() => onAction('dispatch', vehicle)} text="Despachar Vehículo" colorClass="bg-gray-600 hover:bg-gray-700" /> }
                </div>
                {/* Botones secundarios (Devolver/Rechazar) */}
                {(status !== 'En Parqueadero' && status !== 'Llegada OPL' && status !== 'Programado') && (
                    <div className="flex flex-col sm:flex-row space-y-1.5 sm:space-y-0 sm:space-x-2 pt-1.5 mt-1.5 border-t border-gray-100">
                        <button
                            onClick={() => onAction('return_to_previous', vehicle)}
                            className="w-full sm:flex-1 bg-gray-200 text-gray-700 text-xs font-semibold py-1.5 px-3 rounded-md hover:bg-gray-300 flex items-center justify-center space-x-1"
                        >
                            <Undo2 className="w-3 h-3" />
                            <span>Devolver</span>
                        </button>
                        <button onClick={() => onAction('reject', vehicle)} className="w-full sm:flex-1 bg-red-50 border border-red-200 text-red-600 text-xs font-semibold py-1.5 px-3 rounded-md hover:bg-red-100">
                            Rechazar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Tarjeta para mostrar KPIs (Indicadores Clave de Desempeño)
const KPI_Card = ({ title, value, icon, colorClass = "text-[#3a773c]" }) => (
    <div className="bg-white p-4 rounded-lg shadow-md flex items-center space-x-4">
        <div className={`p-3 rounded-full ${colorClass.replace('text', 'bg').replace('[#3a773c]', 'green-100')}`}>{React.cloneElement(icon, { className: `w-6 h-6 ${colorClass}` })}</div>
        <div><p className="text-sm text-gray-500">{title}</p><p className="text-2xl font-bold text-gray-800">{value}</p></div>
    </div>
);

// --- Función para obtener estado anterior ---
// Actualizada para reflejar el estado renombrado y el flujo simplificado
const getPreviousState = (vehicle) => {
    const { status, proceso, location } = vehicle;
    const isBogota = location !== LOCATIONS.FUNZA;

    switch(status) {
        case 'En Planta': return { status: 'En Parqueadero', timestampToClear: 'ingresoPlanta' };
        case 'Peso Inicial': return { status: 'En Planta', timestampToClear: 'ingresoBascula' }; // Renamed state
        case 'En Muelle': 
            if (isBogota) return { status: 'Peso Inicial', timestampToClear: 'asignacionMuelle', clearMuelle: true }; // Renamed previous state
            return { status: 'Llegada OPL', timestampToClear: 'asignacionMuelle', clearMuelle: true };
        case 'Cargando':
        case 'Descargando': return { status: 'En Muelle', timestampToClear: 'inicioOperacion' };
        case 'Pesaje Final': return { status: proceso === 'Cargue' ? 'Cargando' : 'Descargando', timestampToClear: 'finOperacion' }; 
        case 'En Despacho': 
             if (isBogota) return { status: 'Pesaje Final', timestampToClear: 'inicioDespacho' }; 
             // Ajuste para Funza: Si viene de Muelle directo a Despacho, devolver a Muelle (Cargando/Descargando)
             return { status: proceso === 'Cargue' ? 'Cargando' : 'Descargando', timestampToClear: 'inicioDespacho' }; 
        default: return null;
    }
};


// --- Componente de Selección de Ubicación y Login ---
// Pantalla inicial para elegir la sede de operación o Iniciar Sesión
function LocationSelector({ onSelectLocation, isAuth, onLogin }) {
    if (!isAuth) {
         return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
                <img src="/logo-alianza-team.svg" alt="Alianza Team Logo" className="h-24 mb-8" />
                <h1 className="text-3xl font-bold text-[#3a773c] mb-6">Plataforma Gestión de Patios</h1>
                <button
                    onClick={onLogin}
                    className="flex items-center gap-3 bg-[#2F2F2F] text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-black transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                    <img src="https://learn.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.png" alt="Microsoft" className="h-6 w-6"/>
                    Iniciar Sesión con Microsoft
                </button>
            </div>
         );
    }

    const LocationCard = ({ icon, title, location }) => (
        <button
            onClick={() => onSelectLocation(location)}
            className="group flex flex-col items-center justify-center w-64 h-64 bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 ease-in-out border-4 border-transparent hover:border-green-500"
        >
            <div className="p-4 text-gray-500 group-hover:text-green-600 transition-colors duration-300">
                {icon}
            </div>
            <h3 className="mt-4 text-2xl font-bold text-gray-700 group-hover:text-green-700 transition-colors duration-300">{title}</h3>
        </button>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="w-full p-6 flex justify-center">
                <img src="/logo-alianza-team.svg" alt="Alianza Team Logo" className="h-16" />
            </header>
            <main className="flex-grow flex flex-col justify-center items-center text-center px-8">
                <h1 className="text-3xl font-bold text-[#3a773c] mb-2">Bienvenido a la Plataforma Gestión de Patios</h1>
                <p className="text-gray-500 mb-12">Por favor, selecciona tu ubicación para continuar</p>
                <div className="flex flex-wrap justify-center gap-12">
                    <LocationCard
                        icon={<Building size={80} strokeWidth={1.5} />}
                        title="Planta Bogotá"
                        location={LOCATIONS.BOGOTA}
                    />
                    <LocationCard
                        icon={<Warehouse size={80} strokeWidth={1.5} />}
                        title="OPL Funza"
                        location={LOCATIONS.FUNZA}
                    />
                    <LocationCard
                        icon={<Building size={80} strokeWidth={1.5} />}
                        title="Planta Buga"
                        location={LOCATIONS.BUGA}
                    />
                    <LocationCard
                        icon={<Building size={80} strokeWidth={1.5} />}
                        title="Planta Barranquilla"
                        location={LOCATIONS.BARRANQUILLA}
                    />
                </div>
            </main>
        </div>
    );
}

// --- Componente Principal ---
export default function App() {
    // --- ESTADOS PRINCIPALES ---
    // Autenticación y configuración de ubicación
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [isLocationSelected, setIsLocationSelected] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    // Control de vista actual (schedule, registro, entregas, history, dashboard)
    const [view, setView] = useState('schedule');
    // Datos maestros
    const [allProcesosData, setAllProcesosData] = useState([]);

    const [entregasData, setEntregasData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Control de modales
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState({ type: null, data: null });
    // Formularios y estados temporales
    const [newVehicleData, setNewVehicleData] = useState({ planta: currentLocation, operacion: operacionesList[0], proceso: procesosList[0], cliente: '', destino: '', tipoVehiculo: tiposVehiculoList[0], numeroCarga: '', placa: '', transportadora: '', conductor: '', documentoConductor: '', arl: '', vencimientoArl: '', estadoArl: '', fechaProgramacion: getTodayDateString(), pesoProgramado: '', observaciones: '' });
    const [muelle, setMuelle] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    // Filtros
    const [historyFilters, setHistoryFilters] = useState({ placa: '', startDate: '', endDate: '' });
    const [scheduleDateFilters, setScheduleDateFilters] = useState({ startDate: getTodayDateString(), endDate: '' });
    const [schedulePlacaFilter, setSchedulePlacaFilter] = useState('');
    const [scheduleOperacionFilter, setScheduleOperacionFilter] = useState('');
    const [editingEntrega, setEditingEntrega] = useState(null);
    const [editingVehicleData, setEditingVehicleData] = useState(null);
    const [registroPlacaFilter, setRegistroPlacaFilter] = useState('');
    const [registroOperacionFilter, setRegistroOperacionFilter] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleLogin = () => {
        loginWithAzure();
    };

    const handleLogout = () => {
        logout();
        setIsLocationSelected(false); // Reset location selection on logout
        // No need to setIsAuthReady(false) as page reload will handle it
        window.location.href = '/'; // Redirect to home/login page
    };

    // --- Auth Effect ---
    // Verifica si hay un token en la URL (callback) o en localStorage
    useEffect(() => {
        const tokenFromUrl = handleAuthCallback();
        const storedToken = localStorage.getItem('token');
        
        if (tokenFromUrl || storedToken) {
            setIsAuthReady(true);
        } else {
            setIsAuthReady(false);
        }
    }, []);

    // Actualiza la ubicación en el formulario cuando cambia la selección
    useEffect(() => {
        setNewVehicleData(prev => ({ ...prev, planta: currentLocation }));
    }, [currentLocation]);
    
    // Estado para el formulario de entregas
    const [entregaForm, setEntregaForm] = useState({
        searchTerm: '',
        foundVehicle: null,
        items: [{ sku: '', cantidadDespachada: '', cantidadRecibida: '' }],
        message: ''
    });

    // --- Data Loading Effect ---
    // Carga inicial de datos y polling para actualización automática
    useEffect(() => {
        if (!isAuthReady) return;

        const loadData = async () => {
            setIsLoading(true);
            try {
                const [vehicles, entregas] = await Promise.all([
                    fetchVehicles(),
                    fetchEntregas()
                ]);
                setAllProcesosData(vehicles);
                setEntregasData(entregas);
            } catch (error) {
                console.error("Error cargando datos:", error);
                setModalContent({ type: 'notification', data: { title: 'Error de Conexión', message: 'No se pudo conectar con el servidor. Verifica tu conexión o contacta a soporte.' } });
                setIsModalOpen(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
        
        // Polling cada 30 segundos para mantener la vista actualizada
        const interval = setInterval(loadData, 30000);
        return () => clearInterval(interval);
    }, [isAuthReady]);

    // Manejador centralizado de acciones (abrir modales de edición, eliminación, etc.)
    const handleAction = (type, data) => {
        if (type === 'edit_entrega') {
            setEditingEntrega({ ...data });
        }
        if (type === 'edit_scheduled') {
            setEditingVehicleData({ ...data });
        }
        setModalContent({ type, data });
        setIsModalOpen(true);
    };

    // Refresca los datos manualmente desde el servidor
    const refreshData = async () => {
        try {
            const [vehicles, entregas] = await Promise.all([
                fetchVehicles(),
                fetchEntregas()
            ]);
            setAllProcesosData(vehicles);
            setEntregasData(entregas);
        } catch (error) {
            console.error("Error refreshing data:", error);
        }
    };

    // Actualiza el estado de un vehículo y refresca la vista
    const updateVehicleStatus = async (vehicleId, updates) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await updateVehicle(vehicleId, updates);
            await refreshData();
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error updating status:", error);
            alert(`Error al actualizar: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Actualización simple de campos sin cerrar modales (para edición inline)
    const handleSimpleUpdate = async (vehicleId, updates) => {
        try {
            await updateVehicle(vehicleId, updates);
            await refreshData();
        } catch (error) {
            console.error("Error updating field:", error);
        }
    };
    
    // Procesa la edición de un vehículo programado
    const handleUpdateScheduledVehicle = async () => {
        if (!editingVehicleData || !editingVehicleData.id || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const dataToUpdate = { ...editingVehicleData };
            delete dataToUpdate.id;
            delete dataToUpdate.createdAt;
            
            if (dataToUpdate.vencimientoArl) {
                const vencimiento = new Date(dataToUpdate.vencimientoArl + 'T00:00:00');
                const hoy = new Date();
                hoy.setHours(0,0,0,0);
                dataToUpdate.estadoArl = vencimiento >= hoy ? 'ACTIVO' : 'INACTIVO';
            }
            
            await updateVehicle(editingVehicleData.id, dataToUpdate);
            await refreshData();
            setIsModalOpen(false);
            setEditingVehicleData(null);
        } catch (error) {
            console.error("Error actualizando vehículo programado:", error);
            alert(`Error: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Crea un nuevo registro de vehículo programado
    const handleScheduleVehicle = async () => {
        if (!newVehicleData.placa.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            let estadoArlCalculado = newVehicleData.estadoArl;
            if (newVehicleData.vencimientoArl) {
                const vencimiento = new Date(newVehicleData.vencimientoArl + 'T00:00:00');
                const hoy = new Date();
                hoy.setHours(0,0,0,0);
                estadoArlCalculado = vencimiento >= hoy ? 'ACTIVO' : 'INACTIVO';
            }

            const newProcess = {
                ...newVehicleData,
                estadoArl: estadoArlCalculado,
                placa: newVehicleData.placa.trim().toUpperCase(),
                status: 'Programado',
                muelle: null,
                isActive: false,
                timestamps: {},
                location: currentLocation
            };
            await createVehicle(newProcess);
            await refreshData();
            // Reinicia el formulario
            setNewVehicleData({ planta: currentLocation, operacion: operacionesList[0], proceso: procesosList[0], cliente: '', destino: '', tipoVehiculo: tiposVehiculoList[0], numeroCarga: '', placa: '', transportadora: '', conductor: '', documentoConductor: '', arl: '', vencimientoArl: '', estadoArl: '', fechaProgramacion: getTodayDateString(), pesoProgramado: '', observaciones: '' });
            setModalContent({ type: 'notification', data: { title: 'Éxito', message: 'Vehículo programado correctamente.' } });
            // setIsModalOpen(false); // Keep modal open to show notification
        } catch (error) {
            console.error("Error programando vehículo:", error);
            alert(`Error al programar: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Procesa la carga masiva de vehículos desde Excel
    const handleScheduleMassive = async (vehicles) => {
        if (isSubmitting) return false;
        setIsSubmitting(true);
        try {
            const errors = [];
            const validatedVehicles = [];

            vehicles.forEach((v, idx) => {
                const rowNum = idx + 2;
                const rawPlaca = v.Placa || v.placa || v.PLACA;
                const rawFecha = v.FechaProgramacion || v.fechaProgramacion || v['Fecha Programacion'] || v['Fecha Programación'] || v.Cita || v.cita || v.CITA;
                const rawVenc = v['Vencimiento ARL'] || v.VencimientoARL || v.VencimientoArl || v.vencimientoArl || v['Vencimiento Arl'] || v['VENCIMIENTO ARL'];

                const rowErrors = [];

                if (!rawPlaca || String(rawPlaca).trim() === '') {
                    rowErrors.push('La Placa es requerida');
                }

                let fechaProgramacion = null;
                if (rawFecha === null || rawFecha === undefined || String(rawFecha).trim() === '') {
                    rowErrors.push('La FechaProgramacion es requerida');
                } else {
                    fechaProgramacion = formatExcelDate(rawFecha);
                    if (!fechaProgramacion) {
                        rowErrors.push(`La fecha de programación '${rawFecha}' es inválida`);
                    }
                }

                let vencimientoArl = null;
                if (rawVenc !== null && rawVenc !== undefined && String(rawVenc).trim() !== '') {
                    vencimientoArl = formatExcelDate(rawVenc);
                    if (!vencimientoArl) {
                        rowErrors.push(`La fecha de vencimiento '${rawVenc}' es inválida`);
                    }
                }

                if (rowErrors.length > 0) {
                    errors.push(`Fila ${rowNum}: ${rowErrors.join(', ')}`);
                } else {
                    let estadoArlCalculado = v.EstadoARL || v.EstadoArl || '';
                    if (vencimientoArl) {
                        const vencimiento = new Date(vencimientoArl + 'T00:00:00');
                        const hoy = new Date();
                        hoy.setHours(0, 0, 0, 0);
                        estadoArlCalculado = vencimiento >= hoy ? 'ACTIVO' : 'INACTIVO';
                    }

                    const arl = v.ARL || v.Arl || v.arl || '';

                    validatedVehicles.push({
                        planta: v.Ubicacion || v.Ubicación || currentLocation,
                        operacion: v.Operacion || v.Operación || operacionesList[0],
                        proceso: v.Proceso || procesosList[0],
                        cliente: v.Cliente || '',
                        destino: v.Destino || '',
                        tipoVehiculo: v.TipoVehiculo || v['Tipo Vehiculo'] || tiposVehiculoList[0],
                        numeroCarga: v.NumeroCarga || v['Numero Carga'] || v['N° Carga'] || '',
                        placa: String(rawPlaca).trim().toUpperCase(),
                        transportadora: v.Transportadora || '',
                        conductor: v.Conductor || '',
                        documentoConductor: v.DocumentoConductor || v['Documento Conductor'] || '',
                        arl,
                        vencimientoArl,
                        estadoArl: estadoArlCalculado,
                        fechaProgramacion,
                        pesoProgramado: v.PesoProgramado || v['Peso Programado'] || '',
                        observaciones: v.Observaciones || '',
                        status: 'Programado',
                        muelle: null,
                        isActive: false,
                        timestamps: {},
                        location: v.Ubicacion || v.Ubicación || currentLocation
                    });
                }
            });

            if (errors.length > 0) {
                const errorMsg = "Error de validación en la plantilla:\n" + errors.map(e => `- ${e}`).join("\n");
                setModalContent({
                    type: 'notification',
                    data: {
                        title: 'Error de Validación en Carga Masiva',
                        message: errorMsg
                    }
                });
                setIsModalOpen(true);
                return false;
            }

            const promises = validatedVehicles.map(newProcess => createVehicle(newProcess));
            await Promise.all(promises);
            await refreshData();

            setToastMessage(`¡${validatedVehicles.length} vehículos programados con éxito!`);
            setTimeout(() => setToastMessage(""), 4000);
            return true;
        } catch (error) {
            console.error("Error en la carga masiva:", error);
            setModalContent({
                type: 'notification',
                data: {
                    title: 'Error en Carga Masiva',
                    message: error.message || 'No se pudo completar la carga masiva.'
                }
            });
            setIsModalOpen(true);
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    // Registra la llegada de un vehículo (Check-in)
    const handleCheckIn = async (vehicleId, location) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const initialStatus = location !== LOCATIONS.FUNZA ? 'En Parqueadero' : 'Llegada OPL';
        try {
            await updateVehicle(vehicleId, {
                status: initialStatus,
                isActive: true,
                'timestamps.llegadaParqueadero': new Date(),
            });
            await refreshData();
        } catch (error) {
             console.error("Error en CheckIn:", error);
             alert(`Error al registrar llegada: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Elimina un vehículo (Soft delete)
    const handleDeleteVehicle = async (vehicleId) => {
        if (!vehicleId || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await deleteVehicle(vehicleId);
            await refreshData();
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error eliminando vehículo:", error);
            alert(`Error al eliminar: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Rechaza un vehículo registrando la causa
    const handleRejectVehicle = async () => {
         if (!rejectionReason.trim()) {
            setModalContent({type: 'notification', data: { title: 'Error', message: 'Por favor, especifique la causal del rechazo.' }});
            setIsModalOpen(true);
            return;
        }
        if (isSubmitting) return;
        setIsSubmitting(true);
        const { data } = modalContent;
        try {
            await updateVehicle(data.id, {
                status: 'Rechazado',
                isActive: false,
                causalRechazo: rejectionReason,
                'timestamps.rechazo': new Date(),
            });
            await refreshData();
            setRejectionReason('');
            setIsModalOpen(false);
        } catch (error) {
             console.error("Error rechazo:", error);
             alert(`Error al rechazar vehículo: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- CÁLCULO DE DATOS FILTRADOS Y KPIS ---
    const { enParqueadero, enPlanta, pesoInicial, enMuelle, pesajeFinal, enDespacho, historial, kpis, programados, chartData, historicosConEntregas, llegadaOPL } = useMemo(() => { // Added pesoInicial
        const procesosData = allProcesosData.filter(p => {
            const loc = p.location || LOCATIONS.BOGOTA;
            if (!loc || !currentLocation) return false;
            return loc.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() === currentLocation.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        });
        
        let activos = procesosData.filter(p => p.isActive && p.status?.toLowerCase() !== 'programado');
        if (registroPlacaFilter) {
            activos = activos.filter(p => p.placa.toLowerCase().includes(registroPlacaFilter.toLowerCase()));
        }
        if (registroOperacionFilter) {
            activos = activos.filter(p => p.operacion === registroOperacionFilter);
        }

        const inactivos = procesosData.filter(p => !p.isActive && p.status?.toLowerCase() !== 'programado');
        
        const historicos = inactivos.filter(p => p.status === 'Despachado' || p.status === 'Rechazado').sort((a, b) => ((b.timestamps.salida?.seconds || b.timestamps.rechazo?.seconds) || 0) - ((a.timestamps.salida?.seconds || a.timestamps.rechazo?.seconds) || 0));
        
        const historicosConEntregas = [];
        historicos.forEach(v => {
            const vehicleDeliveries = entregasData.filter(e => e.vehicleId === v.id);
            if (vehicleDeliveries.length > 0) {
                vehicleDeliveries.forEach(entrega => {
                    historicosConEntregas.push({ ...v, entregaData: entrega, uniqueId: `${v.id}-${entrega.id}` });
                });
            } else {
                historicosConEntregas.push({ ...v, entregaData: null, uniqueId: v.id });
            }
        });

        const programadosList = procesosData.filter(p => p.status?.toLowerCase() === 'programado');
        const despachados = historicos.filter(p => p.status === 'Despachado');
        const rechazadosCount = historicos.length - despachados.length;

        let totalPatioTime = 0, patioCount = 0, totalMuelleTime = 0, muelleCount = 0;
        const processCounts = {};
        const dailyDispatch = {};
        const processTimes = {};
        let cargues = 0;
        let descargues = 0;
        const carguesPorTipo = {};

        despachados.forEach(v => {
            const { llegadaParqueadero, ingresoPlanta, inicioOperacion, finOperacion, salida } = v.timestamps;
            
            if(llegadaParqueadero && ingresoPlanta) { totalPatioTime += (ingresoPlanta.seconds - llegadaParqueadero.seconds); patioCount++; }
            if(ingresoPlanta && salida) { totalMuelleTime += (salida.seconds - ingresoPlanta.seconds); muelleCount++; }

            processCounts[v.operacion] = (processCounts[v.operacion] || 0) + 1;

            if (v.proceso === 'Descargue') {
                descargues++;
            } else {
                cargues++;
                carguesPorTipo[v.tipoVehiculo] = (carguesPorTipo[v.tipoVehiculo] || 0) + 1;
            }

            if (salida) {
                // Convert timestamp to Date properly in mock/real hybrid
                const dispatchDate = salida.seconds ? new Date(salida.seconds * 1000) : new Date(salida);
                const today = new Date();
                const diffDays = Math.floor((today - dispatchDate) / (1000 * 60 * 60 * 24));
                if (diffDays < 7) {
                    const dateStr = dispatchDate.toISOString().split('T')[0];
                    dailyDispatch[dateStr] = (dailyDispatch[dateStr] || 0) + 1;
                }
            }
            
            if (!processTimes[v.operacion]) processTimes[v.operacion] = { patio: [], operacion: [] };
            if (llegadaParqueadero && ingresoPlanta) processTimes[v.operacion].patio.push(ingresoPlanta.seconds - llegadaParqueadero.seconds);
            if (inicioOperacion && finOperacion) processTimes[v.operacion].operacion.push(finOperacion.seconds - inicioOperacion.seconds);
        });

        const today = new Date(); today.setHours(0,0,0,0);
        // Fix date comparison
        const despachadosHoy = despachados.filter(v => {
            if (!v.timestamps.salida) return false;
            const d = v.timestamps.salida.seconds ? new Date(v.timestamps.salida.seconds * 1000) : new Date(v.timestamps.salida);
            return d >= today;
        }).length;
        
        const pieChartData = Object.entries(processCounts).map(([name, value]) => ({ name, value }));
        const cargaDescargaData = [{ name: 'Cargues', value: cargues }, { name: 'Descargues', value: descargues }];
        const carguesPorTipoData = Object.entries(carguesPorTipo).map(([name, value]) => ({ name, Cargues: value }));
        const completionStatusData = [{ name: 'Despachados', value: despachados.length }, { name: 'Rechazados', value: rechazadosCount }];
        
        const barChartData = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            barChartData.push({ name: d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric' }), Despachos: dailyDispatch[dateStr] || 0 });
        }

        const diferenciasPorCliente = {};
        entregasData.forEach(entrega => {
            if (entrega.cliente) {
                diferenciasPorCliente[entrega.cliente] = (diferenciasPorCliente[entrega.cliente] || 0) + entrega.diferencia;
            }
        });
        const diferenciaPorClienteData = Object.entries(diferenciasPorCliente)
            .map(([name, value]) => ({ cliente: name, diferencia: value }))
            .sort((a, b) => a.diferencia - b.diferencia);

        return {
            llegadaOPL: activos.filter(p => p.status === 'Llegada OPL'),
            enParqueadero: activos.filter(p => p.status === 'En Parqueadero'),
            enPlanta: activos.filter(p => p.status === 'En Planta'),
            pesoInicial: activos.filter(p => p.status === 'Peso Inicial'), // Renamed group
            enMuelle: activos.filter(p => ['En Muelle', 'Cargando', 'Descargando'].includes(p.status)), 
            pesajeFinal: activos.filter(p => p.status === 'Pesaje Final'), 
            enDespacho: activos.filter(p => p.status === 'En Despacho'),
            historial: historicos,
            historicosConEntregas,
            programados: programadosList,
            kpis: { activos: activos.length, avgPatio: formatDuration(totalPatioTime / patioCount), avgMuelle: formatDuration(totalMuelleTime / muelleCount), despachadosHoy: despachadosHoy, rechazados: rechazadosCount },
            chartData: { pieChartData, barChartData, cargaDescargaData, carguesPorTipoData, completionStatusData, diferenciaPorClienteData }
        };
    }, [allProcesosData, entregasData, currentLocation, registroPlacaFilter, registroOperacionFilter]);


    const filteredHistory = useMemo(() => {
        return historicosConEntregas.filter(item => {
            const matchPlaca = item.placa.toLowerCase().includes(historyFilters.placa.toLowerCase());
            // Date handling
            let itemDate = null;
            if (item.timestamps.llegadaParqueadero) itemDate = item.timestamps.llegadaParqueadero.seconds ? new Date(item.timestamps.llegadaParqueadero.seconds * 1000) : new Date(item.timestamps.llegadaParqueadero);
            else if (item.timestamps.rechazo) itemDate = item.timestamps.rechazo.seconds ? new Date(item.timestamps.rechazo.seconds * 1000) : new Date(item.timestamps.rechazo);
            
            if (!itemDate) return false;
            const start = historyFilters.startDate ? new Date(historyFilters.startDate) : null;
            const end = historyFilters.endDate ? new Date(historyFilters.endDate) : null;
            if(start) start.setHours(0,0,0,0);
            if(end) end.setHours(23,59,59,999);
            return matchPlaca && (!start || itemDate >= start) && (!end || itemDate <= end);
        });
    }, [historicosConEntregas, historyFilters]);
    
    // --- ACTUALIZACIÓN DE LÓGICA DE FILTRADO PARA PROGRAMACIÓN ---
    const filteredScheduled = useMemo(() => {
        return programados.filter(item => {
            if (!item.fechaProgramacion) return false; // Hide if no date
            const itemDate = new Date(item.fechaProgramacion + 'T00:00:00');
            const start = scheduleDateFilters.startDate ? new Date(scheduleDateFilters.startDate + 'T00:00:00') : null; // Use scheduleDateFilters
            const end = scheduleDateFilters.endDate ? new Date(scheduleDateFilters.endDate + 'T00:00:00') : null; // Use scheduleDateFilters
            
            const afterStart = start ? itemDate >= start : true;
            const beforeEnd = end ? itemDate <= end : true;
            
            // Nuevos filtros
            const matchPlaca = schedulePlacaFilter ? item.placa.toLowerCase().includes(schedulePlacaFilter.toLowerCase()) : true;
            const matchOperacion = scheduleOperacionFilter ? item.operacion === scheduleOperacionFilter : true;

            return afterStart && beforeEnd && matchPlaca && matchOperacion;
        }).sort((a,b) => new Date(a.fechaProgramacion + 'T00:00:00') - new Date(b.fechaProgramacion + 'T00:00:00'));
    }, [programados, scheduleDateFilters, schedulePlacaFilter, scheduleOperacionFilter]); // Añadir nuevos filtros como dependencias


    const exportToCSV = () => {
        // --- ACTUALIZACIÓN DE CABECERAS CSV ---
        const headers = ["Planta", "Operación", "Proceso", "Cliente", "Destino", "Fecha Prog.", "Tipo Vehículo", "N° Carga", "Placa", 
                         "Transportadora", "Conductor", "Documento", "ARL", "Vencimiento ARL", "Estado ARL", "Peso Prog.", "Estado Final", "Causal Rechazo", "Observaciones",
                         "Llegada Pqdr/OPL", "Ing. Planta", "Ing. Peso Inicial", "Ini. Op. Muelle", "Fin Op. / Ini. Pesaje F.", "Fin Pesaje F. / Ini. Despacho", "Salida", 
                         "T. Pqdr", "T. Planta", "T. Peso Inicial", "T. Muelle", "T. Pesaje F.", "T. Total"]; // Headers ajustados y T. Pesaje F. añadido
        
        const rows = filteredHistory.map(v => {
            const ts = v.timestamps;
            // Helper to get Date object safely
            const getDate = (t) => t ? (t.seconds ? new Date(t.seconds * 1000) : new Date(t)) : null;

            const llegada = getDate(ts.llegadaParqueadero);
            const ingresoPlanta = getDate(ts.ingresoPlanta);
            const ingresoBascula = getDate(ts.ingresoBascula); // Timestamp de Peso Inicial
            const asignacionMuelle = getDate(ts.asignacionMuelle); 
            const inicioOp = getDate(ts.inicioOperacion);
            const finOp = getDate(ts.finOperacion); // Timestamp Fin Op / Inicio Pesaje F
            const inicioDespacho = getDate(ts.inicioDespacho); // Timestamp Fin Pesaje F / Inicio Despacho
            const salida = getDate(ts.salida);
            const rechazo = getDate(ts.rechazo);
            const salidaFinal = salida || rechazo;

            const tiempoParqueadero = (llegada && ingresoPlanta) ? formatDuration((ingresoPlanta - llegada) / 1000) : 'N/A';
            const tiempoEnPlanta = (ingresoPlanta && ingresoBascula) ? formatDuration((ingresoBascula - ingresoPlanta) / 1000) : 'N/A';
            const tiempoPesoInicial = (v.location !== LOCATIONS.FUNZA && ingresoBascula && asignacionMuelle) ? formatDuration((asignacionMuelle - ingresoBascula) / 1000) : 'N/A'; 
            const tiempoOperacion = (inicioOp && finOp) ? formatDuration((finOp - inicioOp) / 1000) : 'N/A'; // Renombrado a T. Muelle implícitamente
            const tiempoPesajeFinal = (finOp && inicioDespacho) ? formatDuration((inicioDespacho - finOp) / 1000) : 'N/A'; 
            const tiempoTotal = (llegada && salidaFinal) ? formatDuration((salidaFinal - llegada) / 1000) : 'N/A';

            const formatDate = (date) => date ? date.toLocaleString('es-CO') : 'N/A';

            // --- AJUSTE DE DATOS PARA CSV ---
            return [v.planta, v.operacion, v.proceso, v.cliente, v.destino, v.fechaProgramacion, v.tipoVehiculo, v.numeroCarga, v.placa, 
                    v.transportadora || '', v.conductor || '', v.documentoConductor || '', v.arl || '', v.vencimientoArl || '', v.estadoArl || '', v.pesoProgramado || '', v.status, v.causalRechazo || '', v.observaciones || '',
                    formatDate(llegada), formatDate(ingresoPlanta), formatDate(ingresoBascula), formatDate(inicioOp), formatDate(finOp), formatDate(inicioDespacho), formatDate(salidaFinal), 
                    tiempoParqueadero, tiempoEnPlanta, tiempoPesoInicial, tiempoOperacion, tiempoPesajeFinal, tiempoTotal].join(','); 
        });
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", "historial_logistico.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- Funciones de Entregas ---
    const handleSearchVehicleByCarga = async () => {
        const term = entregaForm.searchTerm.trim();
        if (!term) {
            setEntregaForm(prev => ({ ...prev, foundVehicle: null, message: '' }));
            return;
        }

        try {
            const vehicle = await fetchVehiculoPorCarga(term);
            setEntregaForm(prev => ({ ...prev, foundVehicle: vehicle, message: '' }));
        } catch (error) {
            setEntregaForm(prev => ({ ...prev, foundVehicle: null, message: error.message || 'No se encontró ningún vehículo despachado en el historial con ese N° de Carga.' }));
        }
    };
    
    const handleDeliveryItemChange = (index, field, value) => {
        const newItems = [...entregaForm.items];
        newItems[index][field] = value;
        setEntregaForm(prev => ({ ...prev, items: newItems }));
    };

    const addDeliveryItemRow = () => {
        setEntregaForm(prev => ({
            ...prev,
            items: [...prev.items, { sku: '', cantidadDespachada: '', cantidadRecibida: '' }]
        }));
    };

    const removeDeliveryItemRow = (index) => {
        const newItems = [...entregaForm.items];
        newItems.splice(index, 1);
        setEntregaForm(prev => ({ ...prev, items: newItems }));
    };

    const handleSaveEntrega = async () => {
        const { foundVehicle, items } = entregaForm;
        if (!foundVehicle || items.length === 0) {
            setEntregaForm(prev => ({...prev, message: 'Busca un vehículo y añade al menos un SKU.'}));
            return;
        }
        if (isSubmitting) return;
        setIsSubmitting(true);

        const promises = items.map(item => {
            const despachada = parseInt(item.cantidadDespachada, 10);
            const recibida = parseInt(item.cantidadRecibida, 10);

            if (!item.sku || isNaN(despachada) || despachada <= 0 || isNaN(recibida) || recibida < 0) {
                return Promise.reject(new Error('Todos los campos son requeridos y las cantidades deben ser números válidos.'));
            }

            const newEntrega = {
                vehicleId: foundVehicle.id,
                numeroCarga: foundVehicle.numeroCarga,
                placa: foundVehicle.placa,
                cliente: foundVehicle.cliente,
                destino: foundVehicle.destino,
                sku: item.sku,
                cantidadDespachada: despachada,
                cantidadRecibida: recibida,
                diferencia: recibida - despachada
            };
            return createEntrega(newEntrega);
        });

        try {
            await Promise.all(promises);
            setModalContent({type: 'notification', data: { title: 'Éxito', message: '¡Entrega guardada correctamente!' }});
            setIsModalOpen(true);
            setEntregaForm({ searchTerm: '', foundVehicle: null, items: [{ sku: '', cantidadDespachada: '', cantidadRecibida: '' }], message: '' });
            // Refresh data to get updated entregas
            await refreshData();
        } catch (error) {
            console.error("Error guardando entrega:", error);
            setModalContent({type: 'notification', data: { title: 'Error', message: `No se pudo guardar la entrega: ${error.message}` }});
            setIsModalOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteEntrega = async (entregaId) => {
        if (!entregaId || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await deleteEntrega(entregaId);
            setIsModalOpen(false);
            await refreshData();
        } catch (error) {
            console.error("Error eliminando entrega:", error);
            setModalContent({type: 'notification', data: { title: 'Error', message: 'No se pudo eliminar el registro.' }});
            setIsModalOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateEntrega = async () => {
        if (!editingEntrega || isSubmitting) return;
        setIsSubmitting(true);

        const despachada = parseInt(editingEntrega.cantidadDespachada, 10);
        const recibida = parseInt(editingEntrega.cantidadRecibida, 10);

        if (!editingEntrega.sku || isNaN(despachada) || despachada <= 0 || isNaN(recibida) || recibida < 0) {
             setModalContent({type: 'notification', data: { title: 'Error de Validación', message: 'Todos los campos son requeridos y las cantidades deben ser números válidos.' }});
             setIsModalOpen(true);
             setIsSubmitting(false);
             return;
        }

        const updatedData = {
            sku: editingEntrega.sku,
            cantidadDespachada: despachada,
            cantidadRecibida: recibida,
            diferencia: recibida - despachada,
        };

        try {
            await updateEntrega(editingEntrega.id, updatedData);
            setIsModalOpen(false);
            setEditingEntrega(null);
            await refreshData();
        } catch (error) {
            console.error("Error actualizando entrega:", error);
            setModalContent({type: 'notification', data: { title: 'Error', message: 'No se pudo actualizar el registro.' }});
            setIsModalOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ==================================================================
    // --- 2. INICIO DE CAMBIOS: renderRegistroView (Opción 2 - Idea B) ---
    // ==================================================================
    const renderRegistroView = () => {
        const renderColumn = (title, data) => (
             <div>
                 <h2 className="text-xl font-bold mb-3">{title} ({data.length})</h2>
                 <div className="bg-gray-50 p-2 rounded-lg shadow-inner h-[58vh] overflow-y-auto"> 
                     {data.length > 0 ? data.map(v => <VehicleCard key={v.id} vehicle={v} onAction={handleAction} onUpdateField={handleSimpleUpdate} />) : <p className="text-gray-500 text-center mt-4">No hay vehículos.</p>}
                 </div>
             </div>
        );

        return (
            <div>
                <div className="mb-4 p-4 border rounded-md bg-white shadow-sm flex items-center space-x-4">
                     <div className="flex items-center space-x-2 flex-grow max-w-xs">
                         <Search className="w-5 h-5 text-gray-400" />
                         <input 
                             type="text" 
                             placeholder="Buscar por Placa..." 
                             value={registroPlacaFilter}
                             onChange={e => setRegistroPlacaFilter(e.target.value)}
                             className="w-full p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                         />
                    </div>
                     <div className="flex items-center space-x-2 flex-grow max-w-xs">
                         <Filter className="w-5 h-5 text-gray-400" />
                         <select 
                             value={registroOperacionFilter} 
                             onChange={e => setRegistroOperacionFilter(e.target.value)}
                             className="w-full p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                         >
                             <option value="">Todas las Operaciones</option>
                             {operacionesList.map(op => <option key={op} value={op}>{op}</option>)}
                         </select>
                    </div>
                </div>

                {currentLocation !== LOCATIONS.FUNZA ? (
                    /*
                     * CAMBIO IMPORTANTE:
                     * Original: grid-cols-1 md:grid-cols-3 lg:grid-cols-6
                     * Nuevo:    grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6
                     *
                     * Esto hace que en laptops (lg) se vean 4 columnas (más ancho)
                     * y solo en pantallas muy grandes (xl) se vean 6.
                     */
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        {renderColumn("En Parqueadero", enParqueadero)}
                        {renderColumn("En Planta", enPlanta)}
                        {renderColumn("Peso Inicial", pesoInicial)} {/* Renamed column */}
                        {renderColumn("En Muelles", enMuelle)}
                        {renderColumn("Pesaje Final", pesajeFinal)} 
                        {renderColumn("Despacho Vh", enDespacho)}
                    </div>
                ) : ( // OPL Funza
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                         {renderColumn("Llegada OPL", llegadaOPL)}
                         {renderColumn("En Muelles", enMuelle)}
                         {renderColumn("Despacho", enDespacho)}
                    </div>
                )}
            </div>
        );
    };
    // ================================================================
    // --- FIN DE CAMBIOS: renderRegistroView ---
    // ================================================================


    const renderContent = () => {
        if (isLoading && !isAuthReady) return <div className="text-center p-10">Cargando datos...</div>;
        switch(view) {
            case 'registro': return renderRegistroView();
            case 'dashboard': return (
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-6">Dashboard de Analítica - {currentLocation}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                        <KPI_Card title="Vehículos Activos" value={kpis.activos} icon={<Truck />} />
                        <KPI_Card title="Tiempo Prom. Parqueadero" value={kpis.avgPatio} icon={<Clock />} />
                        <KPI_Card title="Tiempo Prom. en Muelle" value={kpis.avgMuelle} icon={<Warehouse />} />
                        <KPI_Card title="Despachados Hoy" value={kpis.despachadosHoy} icon={<LogOut />} />
                        <KPI_Card title="Vehículos Rechazados" value={kpis.rechazados} icon={<XCircle />} colorClass="text-red-600" />
                    </div>
                     <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div className="h-80"><h3 className="font-semibold mb-2 text-center">Operaciones Completadas</h3><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={chartData.pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label>{chartData.pieChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
                        <div className="h-80"><h3 className="font-semibold mb-2 text-center">Despachados vs. Rechazados</h3><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={chartData.completionStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} fill="#8884d8" label><Cell key="cell-0" fill="#99c754" /><Cell key="cell-1" fill="#f3b42f" /></Pie><Tooltip /></PieChart></ResponsiveContainer></div>
                        <div className="h-80"><h3 className="font-semibold mb-2 text-center">Cargues por Tipo de Vehículo</h3><ResponsiveContainer width="100%" height="100%"><BarChart data={chartData.carguesPorTipoData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} /><YAxis /><Tooltip /><Bar dataKey="Cargues" fill="#f3b42f" /></BarChart></ResponsiveContainer></div>
                        <div className="h-80"><h3 className="font-semibold mb-2 text-center">Vehículos Despachados (Últimos 7 Días)</h3><ResponsiveContainer width="100%" height="100%"><BarChart data={chartData.barChartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Bar dataKey="Despachos" fill="#99c754" /></BarChart></ResponsiveContainer></div>
                    </div>
                    <div className="h-96">
                        <h3 className="font-semibold mb-2 text-center">Diferencia en Entregas por Cliente (Sobrante / Faltante)</h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.diferenciaPorClienteData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="cliente" angle={-20} textAnchor="end" height={60} interval={0} />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="diferencia" name="Diferencia (Kilos)">
                                    {chartData.diferenciaPorClienteData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.diferencia >= 0 ? '#99c754' : '#f44336'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            );
            case 'schedule': return (
                 <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-bold">Programación de Vehículos - {currentLocation}</h2>
                        <button onClick={() => setView('massive_upload')} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-transform hover:scale-105">
                            <Upload className="w-5 h-5" />
                            <span>Carga Masiva</span>
                        </button>
                    </div>
                    {/* --- FILTROS PARA PROGRAMACIÓN --- */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4 p-4 border rounded-md bg-gray-50 items-end">
                         <div className="sm:col-span-1">
                             <label className="text-xs text-gray-500 block mb-1">Buscar por Placa</label>
                             <div className="relative">
                                 <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                 <input 
                                     type="text" 
                                     placeholder="Placa..." 
                                     value={schedulePlacaFilter}
                                     onChange={e => setSchedulePlacaFilter(e.target.value)}
                                     className="w-full p-2 pl-8 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                 />
                             </div>
                        </div>
                         <div className="sm:col-span-1">
                             <label className="text-xs text-gray-500 block mb-1">Filtrar por Operación</label>
                             <div className="relative">
                                 <Filter className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                 <select 
                                     value={scheduleOperacionFilter} 
                                     onChange={e => setScheduleOperacionFilter(e.target.value)}
                                     className="w-full p-2 pl-8 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white appearance-none"
                                 >
                                     <option value="">Todas</option>
                                     {operacionesList.map(op => <option key={op} value={op}>{op}</option>)}
                                 </select>
                                 <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                             </div>
                        </div>
                        <div className="sm:col-span-1">
                            <label className="text-xs text-gray-500">Desde</label>
                            <input type="date" value={scheduleDateFilters.startDate} onChange={e => setScheduleDateFilters(f => ({...f, startDate: e.target.value}))} className="w-full p-2 border rounded-md"/> {/* Update state variable */}
                        </div>
                         <div className="sm:col-span-1">
                             <label className="text-xs text-gray-500">Hasta</label>
                             <input type="date" value={scheduleDateFilters.endDate} onChange={e => setScheduleDateFilters(f => ({...f, endDate: e.target.value}))} className="w-full p-2 border rounded-md"/> {/* Update state variable */}
                        </div>
                    </div>
                    <div className="h-[60vh] overflow-x-auto"><table className="w-full text-sm text-left text-gray-500"><thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0"><tr>{["Estado", "Fecha Programación", "Cliente", "Ciudad Destino", "Placa", "Operación", "Proceso", "Tipo Vehículo", "N° Carga", "ARL", "Vencimiento", "Estado ARL", "Acción"].map(h => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead><tbody>{filteredScheduled.map(v => (<tr key={v.id} className="bg-white border-b hover:bg-gray-50"><td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${v.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'}`}>{v.isActive ? 'En Operación' : 'Programado'}</span></td><td className="px-4 py-3">{v.fechaProgramacion}</td><td className="px-4 py-3">{v.cliente}</td><td className="px-4 py-3">{v.destino}</td><td className="px-4 py-3 font-medium text-gray-900">{v.placa}</td><td className="px-4 py-3">{v.operacion}</td><td className="px-4 py-3">{v.proceso}</td><td className="px-4 py-3">{v.tipoVehiculo}</td><td className="px-4 py-3">{v.numeroCarga}</td><td className="px-4 py-3">{v.arl || '--'}</td><td className="px-4 py-3">{v.vencimientoArl || '--'}</td><td className="px-4 py-3">
                        <select 
                            value={v.estadoArl || ''} 
                            onChange={(e) => handleSimpleUpdate(v.id, { estadoArl: e.target.value })}
                            className={`p-1 border rounded text-xs font-bold ${v.estadoArl === 'ACTIVO' || v.estadoArl === 'Vigente' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}
                        >
                            <option value="ACTIVO">ACTIVO</option>
                            <option value="INACTIVO">INACTIVO</option>
                        </select>
                    </td>
                    <td className="px-4 py-3">
                        {!v.isActive ? (
                            <div className="flex items-center space-x-2">
                                <button 
                                    onClick={() => {
                                        if (v.estadoArl !== 'ACTIVO' && v.estadoArl !== 'Vigente') {
                                            alert('No se puede registrar llegada: La ARL no está ACTIVA.');
                                            return;
                                        }
                                        handleCheckIn(v.id, v.location || LOCATIONS.BOGOTA);
                                    }} 
                                    className={`text-white text-xs font-semibold py-1 px-3 rounded-md ${(v.estadoArl === 'ACTIVO' || v.estadoArl === 'Vigente') ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-400 cursor-not-allowed'}`}
                                    title={(v.estadoArl !== 'ACTIVO' && v.estadoArl !== 'Vigente') ? 'ARL Inactiva' : 'Registrar Llegada'}
                                >
                                    Registrar Llegada
                                </button>
                                <button onClick={() => handleAction('edit_scheduled', v)} className="p-1 text-blue-600 hover:text-blue-800" title="Editar">
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleAction('delete', v)} className="p-1 text-red-600 hover:text-red-800" title="Eliminar">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (<span className="text-gray-400 text-xs">--</span>)}
                    </td>
                    </tr>))}</tbody></table>{filteredScheduled.length === 0 && <p className="text-center text-gray-500 mt-8">No hay vehículos programados.</p>}</div>
                </div>
            );
            case 'massive_upload':
                return <CargaMasiva onScheduleMassive={handleScheduleMassive} />;
            case 'history': return (
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-4"><h2 className="text-2xl font-bold">Historial de Vehículos - {currentLocation}</h2><button onClick={exportToCSV} className="bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 flex items-center space-x-2"><Download className="w-5 h-5"/><span>Exportar a CSV</span></button></div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 p-4 border rounded-md bg-gray-50"><input type="text" placeholder="Filtrar por placa..." value={historyFilters.placa} onChange={e => setHistoryFilters(f => ({...f, placa: e.target.value}))} className="w-full p-2 border rounded-md"/><input type="date" value={historyFilters.startDate} onChange={e => setHistoryFilters(f => ({...f, startDate: e.target.value}))} className="w-full p-2 border rounded-md" title="Fecha de inicio"/><input type="date" value={historyFilters.endDate} onChange={e => setHistoryFilters(f => ({...f, endDate: e.target.value}))} className="w-full p-2 border rounded-md" title="Fecha de fin"/></div>
                    <div className="h-[60vh] overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-500">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    {["Planta", "Operación", "Proceso", "Cliente", "Destino", "Fecha Prog.", "Tipo Vehículo", "N° Carga", "Placa", 
                                     "Transportadora", "Conductor", "Documento", "ARL", "Vencimiento ARL", "Estado ARL", "Peso Prog.", "Estado Final", "Causal Rechazo", "Observaciones",
                                     "Llegada", "Ing. Planta", "Ing. Peso Inicial", "Ini. Op. Muelle", "Fin Op. / Ini. Pesaje F.", "Fin Pesaje F. / Ini. Despacho", "Salida", 
                                     "T. Pqdr", "T. Planta", "T. Peso Inicial", "T. Muelle", "T. Pesaje F.", "T. Total",
                                     "SKU", "C. Desp.", "C. Recib.", "Dif.", "Acciones"].map(h => 
                                        <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
    {filteredHistory.length > 0 ? filteredHistory.map(v => {
        const ts = v.timestamps;
        const getDate = (t) => t ? (t.seconds ? new Date(t.seconds * 1000) : new Date(t)) : null;

        const llegada = getDate(ts.llegadaParqueadero);
        const ingresoPlanta = getDate(ts.ingresoPlanta);
        const ingresoBascula = getDate(ts.ingresoBascula);
        const asignacionMuelle = getDate(ts.asignacionMuelle); 
        const inicioOp = getDate(ts.inicioOperacion);
        const finOp = getDate(ts.finOperacion);
        const inicioDespacho = getDate(ts.inicioDespacho);
        const salida = getDate(ts.salida);
        const rechazo = getDate(ts.rechazo);
        const salidaFinal = salida || rechazo; 

        const tiempoParqueadero = (llegada && ingresoPlanta) ? formatDuration((ingresoPlanta - llegada) / 1000) : '--';
        const tiempoEnPlanta = (ingresoPlanta && ingresoBascula) ? formatDuration((ingresoBascula - ingresoPlanta) / 1000) : '--';
        const tiempoPesoInicial = (v.location !== LOCATIONS.FUNZA && ingresoBascula && asignacionMuelle) ? formatDuration((asignacionMuelle - ingresoBascula) / 1000) : '--'; 
        const tiempoOperacion = (inicioOp && finOp) ? formatDuration((finOp - inicioOp) / 1000) : '--';
        const tiempoPesajeFinal = (finOp && inicioDespacho) ? formatDuration((inicioDespacho - finOp) / 1000) : '--'; 
        const tiempoTotal = (llegada && salidaFinal) ? formatDuration((salidaFinal - llegada) / 1000) : '--';

        const formatDate = (date) => date ? date.toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : 'N/A';

        return (
            <tr key={v.uniqueId} className="bg-white border-b hover:bg-gray-50">
                <td className="px-4 py-3">{v.planta}</td>
                <td className="px-4 py-3">{v.operacion}</td>
                <td className="px-4 py-3">{v.proceso}</td>
                <td className="px-4 py-3">{v.cliente}</td>
                <td className="px-4 py-3">{v.destino}</td>
                <td className="px-4 py-3">{v.fechaProgramacion}</td>
                <td className="px-4 py-3">{v.tipoVehiculo}</td>
                <td className="px-4 py-3">{v.numeroCarga}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{v.placa}</td>
                <td className="px-4 py-3">{v.transportadora || '--'}</td>
                <td className="px-4 py-3">{v.conductor || '--'}</td>
                <td className="px-4 py-3">{v.documentoConductor || '--'}</td>
                <td className="px-4 py-3">{v.arl || '--'}</td>
                <td className="px-4 py-3">{v.vencimientoArl || '--'}</td>
                <td className="px-4 py-3">{v.estadoArl || '--'}</td>
                <td className="px-4 py-3">{v.pesoProgramado || '--'}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-medium rounded-full ${v.status === 'Rechazado' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{v.status}</span></td>
                <td className="px-4 py-3 text-red-600 text-xs">{v.causalRechazo || '--'}</td>
                <td className="px-4 py-3 text-xs">{v.observaciones || '--'}</td>
                <td className="px-4 py-3 text-xs">{formatDate(llegada)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(ingresoPlanta)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(ingresoBascula)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(inicioOp)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(finOp)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(inicioDespacho)}</td>
                <td className="px-4 py-3 text-xs">{formatDate(salidaFinal)}</td>
                <td className="px-4 py-3 font-mono">{tiempoParqueadero}</td>
                <td className="px-4 py-3 font-mono">{tiempoEnPlanta}</td>
                <td className="px-4 py-3 font-mono">{tiempoPesoInicial}</td>
                <td className="px-4 py-3 font-mono">{tiempoOperacion}</td>
                <td className="px-4 py-3 font-mono">{tiempoPesajeFinal}</td>
                <td className="px-4 py-3 font-mono font-semibold">{tiempoTotal}</td>
                <td className="px-4 py-3">{v.entregaData?.sku || '--'}</td>
                <td className="px-4 py-3">{v.entregaData?.cantidadDespachada || '--'}</td>
                <td className="px-4 py-3">{v.entregaData?.cantidadRecibida || '--'}</td>
                <td className={`px-4 py-3 font-bold ${v.entregaData?.diferencia < 0 ? 'text-red-600' : 'text-green-700'}`}>{v.entregaData?.diferencia ?? '--'}</td>
                <td className="px-4 py-3">
                    <button onClick={() => handleAction('delete', v)} className="p-1 text-red-600 hover:text-red-800" title="Eliminar Registro">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </td>
            </tr>
        );
    }) : (
        <tr><td colSpan={37} className="text-center py-10 text-gray-500">No hay registros en el historial para los filtros seleccionados.</td></tr>
    )}
</tbody>
                        </table>
                    </div>
                </div>
            );
            case 'entregas': {
                 const availableVehicles = allProcesosData.filter(v => v.status === 'Despachado' && (!entregaForm.searchTerm || v.numeroCarga.toLowerCase().includes(entregaForm.searchTerm.toLowerCase())));
                 return (
                 <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold mb-4">Registro de Entregas a Clientes - {currentLocation}</h2>
                    <div className="p-4 border rounded-md bg-gray-50 mb-6">
                        <h3 className="font-semibold text-lg mb-2">Buscar Vehículo por N° de Carga</h3>
                        <div className="flex items-center space-x-2">
                            <input 
                                type="text" 
                                placeholder="Filtrar por N° de Carga..." 
                                value={entregaForm.searchTerm}
                                onChange={e => setEntregaForm(prev => ({...prev, searchTerm: e.target.value}))}
                                className="w-full max-w-sm p-2 border rounded-md"
                            />
                        </div>
                        {entregaForm.message && <p className="text-red-500 text-sm mt-2">{entregaForm.message}</p>}
                    </div>

                    {!entregaForm.foundVehicle && (
                        <div className="mb-6">
                            <h3 className="font-semibold text-lg mb-3">Vehículos Disponibles</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-h-[40vh] overflow-y-auto p-2">
                                {availableVehicles.map(v => (
                                    <div key={v.id} className="p-4 border rounded-md bg-white shadow-sm hover:shadow-md cursor-pointer transition-shadow" onClick={() => setEntregaForm(prev => ({ ...prev, foundVehicle: v, message: '' }))}>
                                        <div className="font-bold text-lg text-blue-600">{v.numeroCarga || 'SIN N°'}</div>
                                        <div className="text-sm text-gray-600 mt-1"><strong>Placa:</strong> {v.placa}</div>
                                        <div className="text-sm text-gray-600"><strong>Cliente:</strong> {v.cliente || '--'}</div>
                                        <div className="text-xs text-gray-500 mt-2 text-right">Click para agregar entrega</div>
                                    </div>
                                ))}
                                {availableVehicles.length === 0 && <p className="text-gray-500 col-span-full text-center py-4">No hay vehículos disponibles que coincidan con la búsqueda.</p>}
                            </div>
                        </div>
                    )}

                    {entregaForm.foundVehicle && (
                        <div className="p-4 border rounded-md bg-green-50 mb-6 animate-fade-in">
                           <div className="flex justify-between items-center mb-3">
                               <h3 className="font-semibold text-lg">Detalles de la Entrega</h3>
                               <button onClick={() => setEntregaForm(prev => ({ ...prev, foundVehicle: null, items: [{ sku: '', cantidadDespachada: '', cantidadRecibida: '' }] }))} className="text-gray-500 hover:text-gray-700" title="Cerrar"><X className="w-5 h-5" /></button>
                           </div>
                           <div className="grid grid-cols-3 gap-4 text-sm mb-4 p-2 bg-white rounded">
                                <div><strong>N° Carga:</strong> {entregaForm.foundVehicle.numeroCarga}</div>
                                <div><strong>Placa:</strong> {entregaForm.foundVehicle.placa}</div>
                                <div><strong>Cliente:</strong> {entregaForm.foundVehicle.cliente}</div>
                           </div>
                           
                           {entregaForm.items.map((item, index) => {
                                const despachada = parseInt(item.cantidadDespachada, 10) || 0;
                                const recibida = parseInt(item.cantidadRecibida, 10) || 0;
                                const diferencia = recibida - despachada;
                                return (
                                    <div key={index} className="grid grid-cols-12 gap-3 items-center mb-2">
                                        <input type="text" placeholder="SKU" value={item.sku} onChange={e => handleDeliveryItemChange(index, 'sku', e.target.value)} className="col-span-4 p-2 border rounded-md text-sm" />
                                        <input type="number" placeholder="Cant. Despachada" value={item.cantidadDespachada} onChange={e => handleDeliveryItemChange(index, 'cantidadDespachada', e.target.value)} className="col-span-2 p-2 border rounded-md text-sm" />
                                        <input type="number" placeholder="Cant. Recibida" value={item.cantidadRecibida} onChange={e => handleDeliveryItemChange(index, 'cantidadRecibida', e.target.value)} className="col-span-2 p-2 border rounded-md text-sm" />
                                        <div className={`col-span-2 p-2 rounded-md text-sm text-center font-semibold ${diferencia < 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                            {diferencia}
                                        </div>
                                        <div className="col-span-2 flex justify-end">
                                        {entregaForm.items.length > 1 && <button onClick={() => removeDeliveryItemRow(index)} className="text-red-500 hover:text-red-700 p-2"><XCircle className="w-5 h-5" /></button>}
                                        </div>
                                    </div>
                                );
                           })}
                           <button onClick={addDeliveryItemRow} className="text-sm bg-gray-200 text-gray-700 py-1 px-3 rounded-md hover:bg-gray-300 mt-2">+ Añadir SKU</button>
                           <div className="text-right mt-4">
                                <button onClick={handleSaveEntrega} className="bg-[#3a773c] text-white font-bold py-2 px-6 rounded-lg hover:bg-green-800">Guardar Entrega</button>
                           </div>
                        </div>
                    )}

                    <div className="mt-8">
                        <h3 className="text-xl font-bold mb-3">Historial de Entregas Registradas</h3>
                        <div className="h-[40vh] overflow-auto"><table className="w-full text-sm text-left text-gray-500"><thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0"><tr>{["N° Carga", "Cliente", "Ciudad Destino", "SKU", "Cant. Despachada", "Cant. Recibida", "Diferencia", "Acciones"].map(h => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead><tbody>{entregasData.map(e => (<tr key={e.id} className="bg-white border-b hover:bg-gray-50"><td className="px-4 py-3">{e.numeroCarga}</td><td className="px-4 py-3">{e.cliente}</td><td className="px-4 py-3">{e.destino}</td><td className="px-4 py-3">{e.sku}</td><td className="px-4 py-3">{e.cantidadDespachada}</td><td className="px-4 py-3">{e.cantidadRecibida}</td><td className={`px-4 py-3 font-bold ${e.diferencia < 0 ? 'text-red-600' : 'text-green-700'}`}>{e.diferencia}</td><td className="px-4 py-3 flex items-center space-x-2"><button onClick={() => handleAction('edit_entrega', e)} className="text-blue-500 hover:text-blue-700"><Pencil className="w-4 h-4" /></button><button onClick={() => handleAction('delete_entrega', e)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button></td></tr>))}</tbody></table></div>
                    </div>
                 </div>
            );
            }
            default: return null;
        }
    };
    
    const renderModalContent = () => {
        const { type, data } = modalContent;
        if (!type) return null;
        const ConfirmationModal = ({ title, message, onConfirm, confirmText, confirmColor }) => (<div><h3 className="text-lg font-bold mb-2">{title}</h3><p className="text-gray-600 mb-6">{message}</p><div className="flex justify-end space-x-3"><button onClick={() => setIsModalOpen(false)} disabled={isSubmitting} className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50">Cancelar</button><button onClick={onConfirm} disabled={isSubmitting} className={`px-4 py-2 text-white font-semibold rounded-md ${confirmColor} disabled:opacity-50`}>{isSubmitting ? 'Procesando...' : confirmText}</button></div></div>);
        const NotificationModal = ({ title, message }) => (<div><h3 className="text-lg font-bold mb-2">{title}</h3><p className="text-gray-600 mb-6">{message}</p><div className="flex justify-end"><button onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Entendido</button></div></div>);
        
        switch (type) {
            case 'notification':
                return <NotificationModal title={data.title} message={data.message} />;
            case 'new_vehicle':
                const handleInputChange = (e) => { const { name, value } = e.target; setNewVehicleData(prev => ({ ...prev, [name]: name === 'placa' ? value.toUpperCase() : value })); };
                return (<div><h3 className="text-lg font-bold mb-4">Programar Vehículo</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input name="planta" value={newVehicleData.planta} readOnly className="w-full p-2 border rounded bg-gray-100" />
                    <select name="operacion" value={newVehicleData.operacion} onChange={handleInputChange} className="w-full p-2 border rounded">{operacionesList.map(op => <option key={op} value={op}>{op}</option>)}</select>
                    <select name="proceso" value={newVehicleData.proceso} onChange={handleInputChange} className="w-full p-2 border rounded">{procesosList.map(op => <option key={op} value={op}>{op}</option>)}</select>
                    <input name="pesoProgramado" value={newVehicleData.pesoProgramado} onChange={handleInputChange} placeholder="Peso Programado (TN)" className="w-full p-2 border rounded"/>
                    <input name="cliente" value={newVehicleData.cliente} onChange={handleInputChange} placeholder="Cliente" className="w-full p-2 border rounded"/>
                    <input name="destino" value={newVehicleData.destino} onChange={handleInputChange} placeholder="Ciudad Destino" className="w-full p-2 border rounded"/>
                    <select name="tipoVehiculo" value={newVehicleData.tipoVehiculo} onChange={handleInputChange} className="w-full p-2 border rounded sm:col-span-2">{tiposVehiculoList.map(v => <option key={v} value={v}>{v}</option>)}</select>
                    <input name="numeroCarga" value={newVehicleData.numeroCarga} onChange={handleInputChange} placeholder="N° de Carga" className="w-full p-2 border rounded"/>
                    <input name="placa" value={newVehicleData.placa} onChange={handleInputChange} placeholder="Placa" className="w-full p-2 border rounded"/>
                    <input name="transportadora" value={newVehicleData.transportadora} onChange={handleInputChange} placeholder="Transportadora" className="w-full p-2 border rounded sm:col-span-2"/>
                    <input name="conductor" value={newVehicleData.conductor} onChange={handleInputChange} placeholder="Nombre del Conductor" className="w-full p-2 border rounded"/>
                    <input name="documentoConductor" value={newVehicleData.documentoConductor} onChange={handleInputChange} placeholder="Documento Conductor" className="w-full p-2 border rounded"/>
                    <input name="arl" value={newVehicleData.arl} onChange={handleInputChange} placeholder="ARL" className="w-full p-2 border rounded"/>
                    <input type="date" name="vencimientoArl" value={newVehicleData.vencimientoArl} onChange={handleInputChange} placeholder="Vencimiento ARL" className="w-full p-2 border rounded" title="Vencimiento ARL"/>
                    <div><label className="text-sm text-gray-600">Fecha Programación</label><input type="date" name="fechaProgramacion" value={newVehicleData.fechaProgramacion} onChange={handleInputChange} className="w-full p-2 border rounded"/></div>
                    <textarea name="observaciones" value={newVehicleData.observaciones} onChange={handleInputChange} placeholder="Observaciones" className="w-full p-2 border rounded sm:col-span-2" rows="3"></textarea>
                </div><button onClick={handleScheduleVehicle} disabled={isSubmitting} className="w-full mt-6 bg-[#3a773c] text-white font-bold py-3 rounded-md hover:bg-green-800 disabled:opacity-50">{isSubmitting ? 'Procesando...' : 'Programar Vehículo'}</button></div>);
            case 'edit_scheduled':
                if (!editingVehicleData) return null;
                const handleEditInputChange = (e) => {
                    const { name, value } = e.target;
                    setEditingVehicleData(prev => ({ ...prev, [name]: name === 'placa' ? value.toUpperCase() : value }));
                };
                return (<div><h3 className="text-lg font-bold mb-4">Editar Programación</h3><div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input name="planta" value={editingVehicleData.planta} onChange={handleEditInputChange} className="w-full p-2 border rounded" />
                    <select name="operacion" value={editingVehicleData.operacion} onChange={handleEditInputChange} className="w-full p-2 border rounded">{operacionesList.map(op => <option key={op} value={op}>{op}</option>)}</select>
                    <select name="proceso" value={editingVehicleData.proceso} onChange={handleEditInputChange} className="w-full p-2 border rounded">{procesosList.map(op => <option key={op} value={op}>{op}</option>)}</select>
                    <input name="pesoProgramado" value={editingVehicleData.pesoProgramado} onChange={handleEditInputChange} placeholder="Peso Programado (TN)" className="w-full p-2 border rounded"/>
                    <input name="cliente" value={editingVehicleData.cliente} onChange={handleEditInputChange} placeholder="Cliente" className="w-full p-2 border rounded"/>
                    <input name="destino" value={editingVehicleData.destino} onChange={handleEditInputChange} placeholder="Ciudad Destino" className="w-full p-2 border rounded"/>
                    <select name="tipoVehiculo" value={editingVehicleData.tipoVehiculo} onChange={handleEditInputChange} className="w-full p-2 border rounded sm:col-span-2">{tiposVehiculoList.map(v => <option key={v} value={v}>{v}</option>)}</select>
                    <input name="numeroCarga" value={editingVehicleData.numeroCarga} onChange={handleEditInputChange} placeholder="N° de Carga" className="w-full p-2 border rounded"/>
                    <input name="placa" value={editingVehicleData.placa} onChange={handleEditInputChange} placeholder="Placa" className="w-full p-2 border rounded"/>
                    <input name="transportadora" value={editingVehicleData.transportadora} onChange={handleEditInputChange} placeholder="Transportadora" className="w-full p-2 border rounded sm:col-span-2"/>
                    <input name="conductor" value={editingVehicleData.conductor} onChange={handleEditInputChange} placeholder="Nombre del Conductor" className="w-full p-2 border rounded"/>
                    <input name="documentoConductor" value={editingVehicleData.documentoConductor} onChange={handleEditInputChange} placeholder="Documento Conductor" className="w-full p-2 border rounded"/>
                    <input name="arl" value={editingVehicleData.arl} onChange={handleEditInputChange} placeholder="ARL" className="w-full p-2 border rounded"/>
                    <input type="date" name="vencimientoArl" value={editingVehicleData.vencimientoArl} onChange={handleEditInputChange} placeholder="Vencimiento ARL" className="w-full p-2 border rounded" title="Vencimiento ARL"/>
                    <div><label className="text-sm text-gray-600">Fecha Programación</label><input type="date" name="fechaProgramacion" value={editingVehicleData.fechaProgramacion} onChange={handleEditInputChange} className="w-full p-2 border rounded"/></div>
                    <textarea name="observaciones" value={editingVehicleData.observaciones} onChange={handleEditInputChange} placeholder="Observaciones" className="w-full p-2 border rounded sm:col-span-2" rows="3"></textarea>
                </div><button onClick={handleUpdateScheduledVehicle} disabled={isSubmitting} className="w-full mt-6 bg-blue-600 text-white font-bold py-3 rounded-md hover:bg-blue-700 disabled:opacity-50">{isSubmitting ? 'Procesando...' : 'Guardar Cambios'}</button></div>);
            case 'return_to_previous': {
                const previousStateInfo = getPreviousState(data);
                if (!previousStateInfo) return null;

                const handleConfirmReturn = () => {
                    const updates = {
                        status: previousStateInfo.status,
                        [`timestamps.${previousStateInfo.timestampToClear}`]: null,
                    };
                    if (previousStateInfo.clearMuelle) {
                        updates.muelle = null;
                    }
                    updateVehicleStatus(data.id, updates);
                };

                return (
                    <ConfirmationModal
                        title="Confirmar Devolución"
                        message={`¿Estás seguro de que quieres devolver el vehículo con N° Carga ${data.numeroCarga} al estado anterior (${previousStateInfo.status})?`}
                        onConfirm={handleConfirmReturn}
                        confirmText="Sí, Devolver"
                        confirmColor="bg-yellow-500 hover:bg-yellow-600"
                    />
                );
            }
            case 'delete_entrega':
                return <ConfirmationModal title="Eliminar Registro de Entrega" message={`¿Estás seguro de que quieres eliminar el registro de entrega para el SKU ${data.sku} y N° Carga ${data.numeroCarga}?`} onConfirm={() => handleDeleteEntrega(data.id)} confirmText="Sí, Eliminar" confirmColor="bg-red-600 hover:bg-red-700" />;
            case 'edit_entrega':
                if (!editingEntrega) return null;
                return (
                    <div>
                        <h3 className="text-lg font-bold mb-4">Editar Entrega</h3>
                        <div className="space-y-4">
                             <div><label className="text-sm font-medium text-gray-700">SKU</label><input type="text" value={editingEntrega.sku} onChange={(e) => setEditingEntrega(prev => ({...prev, sku: e.target.value}))} className="w-full p-2 border rounded" /></div>
                             <div><label className="text-sm font-medium text-gray-700">Cantidad Despachada</label><input type="number" value={editingEntrega.cantidadDespachada} onChange={(e) => setEditingEntrega(prev => ({...prev, cantidadDespachada: e.target.value}))} className="w-full p-2 border rounded" /></div>
                             <div><label className="text-sm font-medium text-gray-700">Cantidad Recibida</label><input type="number" value={editingEntrega.cantidadRecibida} onChange={(e) => setEditingEntrega(prev => ({...prev, cantidadRecibida: e.target.value}))} className="w-full p-2 border rounded" /></div>
                        </div>
                        <button onClick={handleUpdateEntrega} disabled={isSubmitting} className="w-full mt-6 bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50">{isSubmitting ? 'Procesando...' : 'Guardar Cambios'}</button>
                    </div>
                );
            case 'move_to_dock_area':
                return <ConfirmationModal title="Ubicar en Plataforma" message={`¿Confirmas que el vehículo con N° Carga ${data.numeroCarga} (Placa: ${data.placa}) se ha movido al área de muelles?`} onConfirm={() => updateVehicleStatus(data.id, { status: 'En Muelle', 'timestamps.asignacionMuelle': new Date() })} confirmText="Sí, Confirmar" confirmColor="bg-green-500" />;
            case 'assign_dock':
                 return (<div><h3 className="text-lg font-bold mb-4">Asignar Muelle a N° Carga {data.numeroCarga}</h3><input type="text" value={muelle} onChange={e => setMuelle(e.target.value)} placeholder="Número de muelle" className="w-full p-2 border rounded mb-3"/><button onClick={() => updateVehicleStatus(data.id, { muelle: muelle.trim() })} disabled={isSubmitting} className="w-full bg-[#f3b42f] text-white font-bold py-2 px-4 rounded-md hover:bg-yellow-600 disabled:opacity-50">{isSubmitting ? 'Procesando...' : 'Asignar'}</button></div>);
            case 'check_in':
                return <ConfirmationModal title="Registrar Llegada" message={`¿Confirmas la llegada del vehículo con N° Carga ${data.numeroCarga} (Placa: ${data.placa})?`} onConfirm={() => handleCheckIn(data.id, data.location || LOCATIONS.BOGOTA)} confirmText="Sí, Registrar" confirmColor="bg-green-500" />;
            case 'enter_plant':
                return <ConfirmationModal title="Registrar Ingreso a Planta" message={`¿Confirmas el ingreso a planta del vehículo con N° Carga ${data.numeroCarga} (Placa: ${data.placa})?`} onConfirm={() => updateVehicleStatus(data.id, { status: 'En Planta', 'timestamps.ingresoPlanta': new Date() })} confirmText="Sí, Registrar" confirmColor="bg-blue-500" />;
            case 'weigh_vehicle': // Renamed Action
                return <ConfirmationModal title="Iniciar Peso Inicial" message={`¿Confirmas el envío a báscula para el peso inicial del vehículo N° Carga ${data.numeroCarga} (Placa: ${data.placa})?`} onConfirm={() => updateVehicleStatus(data.id, { status: 'Peso Inicial', 'timestamps.ingresoBascula': new Date() })} confirmText="Sí, Enviar" confirmColor="bg-orange-500" />;
            case 'start_operation':
                return <ConfirmationModal title={`Iniciar ${data.proceso}`} message={`¿Confirmas el inicio de la operación para el N° Carga ${data.numeroCarga} (Placa: ${data.placa})?`} onConfirm={() => updateVehicleStatus(data.id, { status: data.proceso === 'Descargue' ? 'Descargando' : 'Cargando', 'timestamps.inicioOperacion': new Date() })} confirmText="Sí, Iniciar" confirmColor="bg-green-500" />;
            case 'end_operation': 
                 if (data.location !== LOCATIONS.FUNZA) {
                     return <ConfirmationModal 
                             title={`Finalizar ${data.proceso}`} 
                             message={`¿Confirmas la finalización de la operación para el N° Carga ${data.numeroCarga} y enviarlo a Pesaje Final?`} 
                             onConfirm={() => updateVehicleStatus(data.id, { 
                                 status: 'Pesaje Final', 
                                 'timestamps.finOperacion': new Date(), 
                             })} 
                             confirmText="Sí, Finalizar y Pesar" 
                             confirmColor="bg-purple-500" 
                         />;
               } else { 
                     return <ConfirmationModal 
                             title={`Finalizar ${data.proceso}`} 
                             message={`¿Confirmas la finalización de la operación para el N° Carga ${data.numeroCarga} y enviarlo a Despacho?`} 
                             onConfirm={() => updateVehicleStatus(data.id, { 
                                 status: 'En Despacho', 
                                 'timestamps.finOperacion': new Date(), 
                                 'timestamps.inicioDespacho': new Date() 
                             })} 
                             confirmText="Sí, Finalizar y Enviar" 
                             confirmColor="bg-purple-500" 
                         />;
               }
            case 'final_weighing_complete': 
                 return <ConfirmationModal 
                         title="Completar Pesaje Final" 
                         message={`¿Confirmas que el pesaje final para el N° Carga ${data.numeroCarga} se ha completado y está listo para Despacho?`} 
                         onConfirm={() => updateVehicleStatus(data.id, { 
                             status: 'En Despacho', 
                             'timestamps.inicioDespacho': new Date() 
                         })} 
                         confirmText="Sí, Completar y Despachar" 
                         confirmColor="bg-teal-500" 
                     />;
            case 'dispatch':
                return <ConfirmationModal title="Despachar Vehículo" message={`¿Estás seguro de despachar el vehículo con N° Carga ${data.numeroCarga} (Placa: ${data.placa})?`} onConfirm={() => updateVehicleStatus(data.id, { isActive: false, status: 'Despachado', 'timestamps.salida': new Date() })} confirmText="Sí, Despachar" confirmColor="bg-gray-600" />;
            case 'reject':
                return (<div><h3 className="text-lg font-bold mb-4 text-red-600">Rechazar Vehículo N° Carga {data.numeroCarga}</h3><textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Escribe la causal del rechazo..." className="w-full p-2 border rounded mb-3" rows="4"></textarea><button onClick={handleRejectVehicle} disabled={isSubmitting} className="w-full bg-red-600 text-white font-bold py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50">{isSubmitting ? 'Procesando...' : 'Confirmar Rechazo'}</button></div>);
            case 'delete':
                return <ConfirmationModal title="Eliminar Registro" message={`¿Estás seguro de que quieres eliminar permanentemente el registro del vehículo con N° Carga ${data.numeroCarga} (Placa: ${data.placa})? Esta acción no se puede deshacer.`} onConfirm={() => handleDeleteVehicle(data.id)} confirmText="Sí, Eliminar" confirmColor="bg-red-600 hover:bg-red-700" />;
            default: return null;
        }
    };
    
    const handleLocationSelect = (location) => {
        setCurrentLocation(location);
        setIsLocationSelected(true);
        setView('schedule');
    };

    if (!isLocationSelected) {
        return <LocationSelector
            isAuth={isAuthReady}
            onLogin={handleLogin}
            onSelectLocation={(loc) => { setCurrentLocation(loc); setIsLocationSelected(true); }}
        />;
    }

    return (
        <div className="bg-slate-50 min-h-screen font-sans">
            <header className="bg-white shadow-md sticky top-0 z-10 border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        <img src="/logo-alianza-team.svg" alt="Alianza Team Logo" className="h-10" />
                        <div className="border-l pl-4 ml-2">
                            <button 
                                onClick={() => setIsLocationSelected(false)} 
                                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                {currentLocation !== LOCATIONS.FUNZA ? <Building className="w-5 h-5 text-gray-600" /> : <Warehouse className="w-5 h-5 text-gray-600" />}
                                <span className="font-semibold text-gray-700">Inicio</span>
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <nav className="bg-gray-200 p-1 rounded-lg flex items-center">
                            <button onClick={() => setView('schedule')} className={`px-3 py-1.5 text-sm font-semibold rounded-md flex items-center space-x-2 ${view === 'schedule' ? 'bg-white shadow' : 'text-gray-600'}`}><Calendar className="w-4 h-4"/><span>Programación</span></button>
                            <button onClick={() => setView('registro')} className={`px-3 py-1.5 text-sm font-semibold rounded-md flex items-center space-x-2 ${view === 'registro' ? 'bg-white shadow' : 'text-gray-600'}`}><List className="w-4 h-4"/><span>Registro</span></button>
                            <button onClick={() => setView('entregas')} className={`px-3 py-1.5 text-sm font-semibold rounded-md flex items-center space-x-2 ${view === 'entregas' ? 'bg-white shadow' : 'text-gray-600'}`}><FileCheck className="w-4 h-4"/><span>Entregas</span></button>
                            <button onClick={() => setView('history')} className={`px-3 py-1.5 text-sm font-semibold rounded-md flex items-center space-x-2 ${view === 'history' ? 'bg-white shadow' : 'text-gray-600'}`}><Search className="w-4 h-4"/><span>Historial</span></button>
                            <button onClick={() => setView('dashboard')} className={`px-3 py-1.5 text-sm font-semibold rounded-md flex items-center space-x-2 ${view === 'dashboard' ? 'bg-white shadow' : 'text-gray-600'}`}><BarChart2 className="w-4 h-4"/><span>Dashboard</span></button>
                        </nav>
                         <div className="flex items-center space-x-4"> {/* Contenedor para alinear los botones */}
                            {view !== 'massive_upload' && (
                                <button onClick={() => handleAction('new_vehicle')} className="bg-[#3a773c] text-white font-bold py-2 px-4 rounded-lg hover:bg-green-800 flex items-center space-x-2 transition-transform hover:scale-105"><PlusCircle className="w-5 h-5" /><span>Programar Vehículo</span></button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 flex items-center space-x-2 transition-transform hover:scale-105"
                            >
                                <LogOut className="w-5 h-5" />
                                <span>Cerrar Sesión</span>
                            </button>
                         </div>
                    </div>
                </div>
            </header>
            <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">{renderContent()}</main>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>{renderModalContent()}</Modal>
            {toastMessage && (<div className="fixed bottom-5 right-5 bg-green-500 text-white py-3 px-5 rounded-lg shadow-xl z-50 fade-in">{toastMessage}</div>)}
        </div>
    );
}
